import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ensureHubSpotScript, createHubSpotForm, HUBSPOT_LIGHT_CSS } from "@/lib/hubspot";

const HubSpotBookDemoForm = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!containerRef.current) return;
      containerRef.current.innerHTML = "";
      ensureHubSpotScript(() => {
        if (!containerRef.current) return;
        containerRef.current.innerHTML = `<div id="book-demo-hs-form"></div>`;
        createHubSpotForm({
          region: "eu1",
          portalId: "25538277",
          formId: "737eb664-c3f6-4c61-834f-300ca3ddff05",
          target: "#book-demo-hs-form",
          css: HUBSPOT_LIGHT_CSS,
        });
      });
    }, 100);
    return () => clearTimeout(timeout);
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}>
      <div ref={containerRef} className="min-h-[200px] [&_.hs-form-frame]:w-full" />
    </motion.div>
  );
};

const ease = [0.16, 1, 0.3, 1] as const;

const BookDemo = () => {
  const { t, i18n } = useTranslation('common');

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('bookDemoPage.meta.title')}</title>
        <meta name="description" content={t('bookDemoPage.meta.description')} />
        <link rel="canonical" href="https://cetasol.com/book-a-demo" />
        <meta property="og:title" content={t('bookDemoPage.meta.title')} />
        <meta property="og:description" content={t('bookDemoPage.meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/book-a-demo" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('bookDemoPage.meta.title')} />
        <meta name="twitter:description" content={t('bookDemoPage.meta.description')} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
      </Helmet>
      <Header />
      <main className="pt-24 pb-0">
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-5xl">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6 leading-[1.1]">
                  {t('bookDemoPage.heading')}
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {t('bookDemoPage.description')}
                </p>
              </motion.div>
              <HubSpotBookDemoForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BookDemo;
